import { createFileRoute, Link } from "@tanstack/react-router";

import { PageHero, Prose, ProseSection } from "@/components/page-hero";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/help")({
  head: () => ({
    meta: [
      { title: "Help Centre — Quibblies" },
      {
        name: "description",
        content:
          "Answers about joining Quibblies, account approval, submitting publications for review, profile image decisions, pen names, appeals and exporting your work.",
      },
      { property: "og:title", content: "Quibblies Help Centre" },
      {
        property: "og:description",
        content:
          "How joining, review, publishing and appeals work on Quibblies, in plain language.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: HelpPage,
});

const FAQ = [
  {
    q: "What can I publish on Quibblies?",
    a: "Fiction, personal experiences, personal reflections, diaries and journals, poetry, series and other writing. You choose a content type when you create a publication, and readers see that label on the card and the page.",
  },
  {
    q: "How do you handle writing about real people?",
    a: "Personal writing must protect your privacy and other people's. Change or omit names, addresses, workplaces, schools, exact locations and social accounts. Moderators can raise privacy, personal-information or consent concerns and ask for changes before publication.",
  },
  {
    q: "How long does account approval take?",
    a: "Most applications are reviewed within a few days. You will be emailed when your account is approved or declined, and you can read stories while you wait.",
  },
  {
    q: "Why is my publication not live yet?",
    a: "Nothing publishes automatically. A submitted publication moves to Reviewing when a moderator claims it, then to Approved, Changes requested or Rejected — whether it is fiction, a personal experience, a reflection or poetry.",
  },
  {
    q: "I edited a published publication — what happens?",
    a: "The published version stays live. Your edited version goes back through review as a new version, and only replaces the live one once approved.",
  },
  {
    q: "Can I publish one chapter while writing the rest?",
    a: "Yes. Chapters are reviewed individually, so Chapter 1 can be live while later chapters are still drafts or awaiting review.",
  },
  {
    q: "My avatar or cover image was declined.",
    a: "Profile media is reviewed separately from your account. A declined image does not affect your membership; upload a replacement or appeal the decision.",
  },
  {
    q: "How do I appeal a decision?",
    a: "Every decision notice includes an appeal link. Appeals go to a senior administrator, and the moderator who made the original decision cannot review it.",
  },
  {
    q: "Can I use a pen name?",
    a: "Yes. You can publish under one or more pen names; your legal name, email, date of birth and account details never appear on your public author profile.",
  },
  {
    q: "How do I download my writing?",
    a: "Account settings include a full export of your publications, chapters and account data.",
  },
];

function HelpPage() {
  return (
    <div>
      <PageHero
        eyebrow="Help Centre"
        title="How Quibblies works"
        description="Joining, review, publishing, appeals and exports — explained without the jargon."
      />
      <Prose>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map((item, index) => (
            <AccordionItem key={item.q} value={`item-${index}`}>
              <AccordionTrigger className="text-left font-medium text-foreground">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <ProseSection title="Still stuck?">
          <p>
            <Link to="/contact" className="text-primary-glow underline-offset-4 hover:underline">
              Contact the Quibblies team
            </Link>{" "}
            and tell us what happened. For copyright claims, use the{" "}
            <Link to="/copyright" className="text-primary-glow underline-offset-4 hover:underline">
              copyright and content removal
            </Link>{" "}
            process instead — it is handled separately from ordinary reports.
          </p>
        </ProseSection>
      </Prose>
    </div>
  );
}
